/**
 * Admin Dashboard — summary cards and recent activity
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { formatProjectName } from '../../../shared/formatProjectName';

interface Stats {
  totalProjects: number;
  totalClients: number;
  totalUsers: number;
  totalFiles: number;
  recentProjects: any[];
}

export function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    api.admin.stats()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: '#94a3b8' }}>Loading dashboard...</div>;
  if (!stats) return <div style={{ color: '#ef4444' }}>Failed to load dashboard</div>;

  return (
    <div>
      <h1 style={{
        ...styles.heading,
        ...(isMobile ? { fontSize: 20 } : {}),
      }}>Dashboard</h1>

      {/* Summary cards */}
      <div style={{
        ...styles.cards,
        ...(isMobile ? { gridTemplateColumns: '1fr 1fr' } : {}),
      }}>
        <StatCard label="Projects" value={stats.totalProjects} to="/admin/projects" />
        <StatCard label="Clients" value={stats.totalClients} to="/admin/clients" />
        <StatCard label="Users" value={stats.totalUsers} to="/admin/users" />
        <StatCard label="Files" value={stats.totalFiles} />
      </div>

      {/* Recent projects */}
      <h2 style={styles.sectionTitle}>Recent Projects</h2>
      <div style={styles.table}>
        {!isMobile && (
          <div style={styles.tableHeader}>
            <span style={{ flex: 1 }}>Name</span>
            <span style={{ width: 140 }}>Client</span>
            <span style={{ width: 120 }}>Location</span>
            <span style={{ width: 100, textAlign: 'right' }}>Updated</span>
          </div>
        )}
        {stats.recentProjects.map((p) => (
          <Link
            key={p.id}
            to={`/admin/projects/${p.id}`}
            style={{
              ...styles.row,
              ...(isMobile ? { flexDirection: 'column', alignItems: 'flex-start', gap: 4, padding: '12px 16px' } : {}),
            }}
          >
            <span style={styles.rowName}>{formatProjectName(p)}</span>
            {isMobile ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                {p.clientName && <span style={{ color: '#4F7CFF', fontSize: 12, fontWeight: 500 }}>{p.clientName}</span>}
                <span style={{ color: '#475569', fontSize: 11 }}>{new Date(p.updatedAt).toLocaleDateString()}</span>
              </div>
            ) : (
              <>
                <span style={styles.rowMeta}>{p.clientName || '—'}</span>
                <span style={styles.rowMeta}>
                  {p.city}{p.state ? `, ${p.state}` : ''}
                  {!p.city && !p.state && '—'}
                </span>
                <span style={styles.rowDate}>{new Date(p.updatedAt).toLocaleDateString()}</span>
              </>
            )}
          </Link>
        ))}
        {stats.recentProjects.length === 0 && (
          <div style={{ padding: 16, color: '#64748b', fontSize: 14 }}>No projects yet</div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, to }: { label: string; value: number; to?: string }) {
  const card = (
    <div style={styles.card}>
      <div style={styles.cardValue}>{value}</div>
      <div style={styles.cardLabel}>{label}</div>
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none' }}>{card}</Link> : card;
}

const styles: Record<string, React.CSSProperties> = {
  heading: {
    fontSize: 24,
    fontWeight: 700,
    color: '#f1f5f9',
    margin: '0 0 24px',
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 16,
    marginBottom: 32,
  },
  card: {
    background: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: 12,
    padding: '20px 24px',
  },
  cardValue: {
    fontSize: 32,
    fontWeight: 700,
    color: '#f1f5f9',
  },
  cardLabel: {
    fontSize: 13,
    color: '#64748b',
    marginTop: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#f1f5f9',
    margin: '0 0 12px',
  },
  table: {
    background: '#1a1a1a',
    border: '1px solid #333333',
    borderRadius: 12,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 20px',
    borderBottom: '1px solid #333333',
    color: '#64748b',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    gap: 16,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 20px',
    borderBottom: '1px solid #333333',
    textDecoration: 'none',
    color: 'inherit',
    gap: 16,
  },
  rowName: {
    flex: 1,
    fontWeight: 500,
    color: '#f1f5f9',
    fontSize: 14,
  },
  rowMeta: {
    color: '#64748b',
    fontSize: 13,
    width: 120,
  },
  rowDate: {
    color: '#64748b',
    fontSize: 13,
    width: 100,
    textAlign: 'right' as const,
  },
};
